@if (!planOverlayHidden()) {
<dialog
  open
  class="upgrade-modal-overlay upgrade-modal-live"
  aria-labelledby="subscription-upgrade-title"
  (cancel)="onDialogCancel($event)">
  <div class="upgrade-modal-panel upgrade-modal-dialog">
    <div class="upgrade-modal-header">
      <div>
        <h2 id="subscription-upgrade-title" class="upgrade-modal-title">
          {{ titleText() }}
        </h2>
        <p class="upgrade-modal-subtitle">{{ subtitleText() }}</p>
      </div>
      @if (closable()) {
        <button
          type="button"
          class="modal-close-btn"
          aria-label="Close"
          [disabled]="paymentProcessing()"
          (click)="dismiss()">
          <svg
            class="modal-close-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2.25"
              stroke-linecap="round" />
          </svg>
        </button>
      }
    </div>

    <div class="upgrade-modal-body small-scrollbar">
      @if (loading() && !visiblePlans().length) {
        <p class="ma-loading" style="min-height: 180px">Loading plans…</p>
      } @else {
        @if (!closable()) {
          <div class="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            @if (summary()?.plan?.isFree) {
              Free trial includes limited projects and builder connections. Upgrade to unlock full access.
            } @else {
              Your assigned plan trial has ended. Complete payment to continue with
              <strong>{{ summary()?.plan?.name }}</strong> and all included features.
            }
          </div>
        }

        @if (hasQuarterlyPricing() || hasYearlyPricing()) {
          <div class="billing-toggle-bar upgrade-billing-bar">
            <span class="billing-toggle-label">Billing</span>
            <div class="billing-toggle" role="tablist" aria-label="Billing cycle">
              <button
                type="button"
                class="billing-btn"
                [class.active]="billingCycle() === 'MONTHLY'"
                (click)="setBillingCycle('MONTHLY')">
                Monthly
              </button>
              @if (hasQuarterlyPricing()) {
                <button
                  type="button"
                  class="billing-btn"
                  [class.active]="billingCycle() === 'QUARTERLY'"
                  (click)="setBillingCycle('QUARTERLY')">
                  Quarterly
                </button>
              }
              @if (hasYearlyPricing()) {
                <button
                  type="button"
                  class="billing-btn"
                  [class.active]="billingCycle() === 'YEARLY'"
                  (click)="setBillingCycle('YEARLY')">
                  Yearly
                  <span class="billing-save-tag">Save</span>
                </button>
              }
            </div>
          </div>
        }

        <div class="upgrade-plans-grid">
          @for (plan of visiblePlans(); track plan.id) {
            <button
              type="button"
              class="upgrade-plan-card"
              [class.is-current]="isCurrentPlan(plan)"
              [class.is-selected]="selectedPlanId() === plan.id"
              [class.is-recommended]="recommendedPlanId() === plan.id && selectedPlanId() !== plan.id"
              (click)="selectPlan(plan)">
              <div class="upc-header">
                <div class="upc-badge-row">
                  <div>
                    <div class="upc-name">{{ plan.name }}</div>
                    <div class="upc-code">{{ plan.code }}</div>
                  </div>
                  <span class="upc-check" aria-hidden="true">
                    <i class="fa-solid fa-check text-[10px]"></i>
                  </span>
                </div>
                <div class="upc-badge-row">
                  <div class="upc-price-row">
                    <span class="upc-price">
                      {{ planAmount(plan) | currency: 'INR' : 'symbol' : '1.0-0' }}
                    </span>
                    <span class="upc-price-sfx">{{ cycleSuffix() }}</span>
                  </div>
                  @if (isCurrentPlan(plan)) {
                    <span class="badge-current">Current</span>
                  } @else if (recommendedPlanId() === plan.id) {
                    <span class="badge-recommended">Popular</span>
                  }
                </div>
              </div>
              <div class="upc-body">
                @if (plan.tagline) {
                  <p class="upc-tagline">{{ plan.tagline }}</p>
                }
                <div class="upc-limit-row">
                  <i class="fa-solid fa-building"></i>
                  <span>{{ formatLimit(plan.projectLimit) }} projects</span>
                </div>
                <div class="upc-limit-row">
                  <i class="fa-solid fa-users"></i>
                  <span>{{ formatLimit(plan.userLimit) }} builder connections</span>
                </div>
              </div>
              <div class="upc-footer">
                <span class="upc-limits">
                  {{ selectedPlanId() === plan.id ? 'Selected' : 'Tap to select' }}
                </span>
                <span class="btn-select-plan">
                  {{ isCurrentPlan(plan) ? 'Continue' : 'Choose' }}
                </span>
              </div>
            </button>
          } @empty {
            <p class="col-span-full text-sm text-slate-500">
              No paid plans are available yet. Contact your administrator.
            </p>
          }
        </div>
      }
    </div>

    <div class="upgrade-modal-footer">
      <div class="upgrade-modal-footer-copy">
        <div class="upgrade-modal-footer-label">
          {{ selectedPlan() ? 'Selected plan' : 'No plan selected' }}
        </div>
        <div class="upgrade-modal-footer-value">
          @if (selectedPlan(); as plan) {
            {{ plan.name }} ·
            {{ planAmount(plan) | currency: 'INR' : 'symbol' : '1.0-0' }}
            {{ cycleSuffix() }}
          } @else {
            Choose a plan to continue
          }
        </div>
      </div>
      <button
        type="button"
        class="btn-select-plan"
        [disabled]="!selectedPlan() || upgradingPlanId() !== null || paymentProcessing()"
        (click)="continueToPayment()">
        @if (upgradingPlanId() || paymentProcessing()) {
          Processing…
        } @else {
          Continue to payment
        }
      </button>
    </div>
  </div>
</dialog>
}

@if (paymentConfirm(); as confirm) {
  <app-subscription-payment-confirm-modal
    [title]="confirm.title"
    [description]="confirm.description"
    [baseAmount]="confirm.baseAmount"
    [gstRate]="confirm.gstRate"
    [gstAmount]="confirm.gstAmount"
    [amount]="confirm.amount"
    [billingLabel]="confirm.billingLabel ?? ''"
    [processing]="paymentProcessing()"
    (cancel)="cancelPaymentConfirm()"
    (proceed)="proceedPaymentConfirm()" />
}
